import React, { useEffect, useRef, useState } from 'react';
import ModalHeader from '@Client/runner.shared/modal/Header/Header';
import ModalFooter from '@Client/runner.shared/modal/Footer/Footer';
import { useService } from '@Client/runner.hooks/useService';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { Guid } from '@Shared.Angular/@types/guid';
import ITagDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagDetail';
import { TagGroupValidationErrorType } from '@Shared.Angular/@types/core/contracts/constants/tagGroupValidationErrorType';
import { ITagGroupValidationError } from '@Shared.Angular/@types/core/contracts/exceptions/tagGroupValidationError';
import ColorPalette from '../../ColorPalette/ColorPalette';
import TagSelector from '../../TagSelector/TagSelector';

export type Props = {
  id: Guid;
  name: string;
  description: string;
  color: string;
  tags: ITagDetail[];
  modalTitle: string;
  saveSuccess: () => void;
  closeModal: () => void;
};

const defaultColors = [
  'E15F45',
  '6D9BD0',
  '856CAA',
  'D298A5',
  '55B1A5',
  'A1C197',
  'E0B346',
  'E98066',
  '49A270',
  '516B75',
  '37769B',
  'B66578',
  'DA9254',
  '819A79',
  'F06F72',
  '63A693'
];

const TagGroupEditModal: React.FC<Props> = ({
  id,
  name,
  description,
  color,
  tags,
  modalTitle,
  saveSuccess,
  closeModal
}) => {
  const [nameInput, setNameInput] = useState(name);
  const [descriptionInput, setDescriptionInput] = useState(description);
  const [colorInput, setColorInput] = useState(color || defaultColors[0]);
  const [tagsInput, setTagsInput] = useState<ITagDetail[]>(tags);
  const [hasNameError, setHasNameError] = useState(false);
  const [tagExistsError, setTagExistsError] = useState('');
  const [errorMessagesOnSubmit, setErrorMessagesOnSubmit] = useState<string[]>(
    []
  );
  const [colors, setColors] = useState(defaultColors);

  const notificationService = useService<SharedAngular.NotificationService>(
    'notificationService'
  );
  const appInsightsService =
    useService<SharedAngular.AppInsightsService>('appInsightsService');
  const tagGroupApiService =
    useService<SharedAngular.TagGroupApiService>('tagGroupApiService');

  const originalTagIndexMap = useRef<
    Map<Guid, { name: string; index: number }>
  >(new Map());

  useEffect(() => {
    const tagIndexMap = new Map<Guid, { name: string; index: number }>();
    tags.forEach((tag, index) =>
      tagIndexMap.set(tag.id, { name: tag.name, index })
    );
    originalTagIndexMap.current = tagIndexMap;
  }, [tags]);

  useEffect(() => {
    refreshColors(colorInput);
  }, [color, colorInput]);

  const handleNameChange = (event) => {
    setNameInput(event.target.value);
    setHasNameError(false);
  };

  const handleDescriptionChange = (event) => {
    setDescriptionInput(event.target.value);
  };

  const save = async () => {
    if (!nameInput) {
      setHasNameError(true);
      return;
    }

    appInsightsService.startEventTimer('setupTaggroupsSaved');

    try {
      await tagGroupApiService.save({
        id,
        name: nameInput,
        description: descriptionInput,
        color: colorInput,
        tags: tagsInput
      });

      saveSuccess();
      notificationService.showSuccessToast('Tag Group saved successfully');
    } catch (error) {
      handleError(error);
    }
  };

  const handleError = (error) => {
    const tagsToRestore: { id: Guid; name: string; index: number }[] = [];

    error.data.errors.forEach((error: ITagGroupValidationError) => {
      if (error.type === TagGroupValidationErrorType.UsedTagsCannotBeDeleted) {
        const id = error.usedTagId;
        const info = originalTagIndexMap.current.get(id);
        if (info) {
          tagsToRestore.push({ id, name: info.name, index: info.index });
        }
      }
    });

    if (tagsToRestore.length > 0) {
      tagsToRestore.sort((a, b) => a.index - b.index);
      setTagsInput((prev) => {
        const newTags = [...prev];
        tagsToRestore.forEach((tag) => {
          if (!newTags.some((t) => t.id === tag.id)) {
            newTags.splice(tag.index, 0, { id: tag.id, name: tag.name });
          }
        });
        return newTags;
      });
    }

    const usedTagErrors = error.data.errors.filter(
      (e: ITagGroupValidationError) =>
        e.type === TagGroupValidationErrorType.UsedTagsCannotBeDeleted
    );

    const otherErrors = error.data.errors.filter(
      (e: ITagGroupValidationError) =>
        e.type !== TagGroupValidationErrorType.UsedTagsCannotBeDeleted
    );

    usedTagErrors.sort(
      (a: ITagGroupValidationError, b: ITagGroupValidationError) => {
        const aIndex = originalTagIndexMap.current.get(a.usedTagId)?.index ?? 0;
        const bIndex = originalTagIndexMap.current.get(b.usedTagId)?.index ?? 0;
        return aIndex - bIndex;
      }
    );

    setErrorMessagesOnSubmit([
      ...otherErrors.map((e: ITagGroupValidationError) => e.message),
      ...usedTagErrors.map((e: ITagGroupValidationError) => e.message)
    ]);
  };

  const refreshColors = (color: string) => {
    setColors((prevColors) => {
      const updatedColors = [...prevColors];
      if (
        defaultColors.includes(color) &&
        updatedColors.length > defaultColors.length
      ) {
        updatedColors.pop();
      }
      if (!updatedColors.includes(color)) {
        if (updatedColors.length > defaultColors.length) {
          updatedColors.pop();
        }
        updatedColors.push(color);
      }
      return updatedColors;
    });

    setColorInput(color);
  };

  return (
    <div className="tag-group-edit-modal">
      <ModalHeader title={modalTitle} />

      {errorMessagesOnSubmit.length > 0 && (
        <div className="tag-group-error validation-errors">
          <ul>
            {errorMessagesOnSubmit.map((msg, idx) => (
              <li key={idx} className="error-message-on-submit">
                {msg}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tagExistsError && (
        <div className="tag-group-error validation-errors">
          <p>{tagExistsError}</p>
        </div>
      )}

      <div className="input-field">
        <label htmlFor="category-name">Group Name</label>
        <input
          id="category-name"
          type="text"
          value={nameInput}
          onChange={handleNameChange}
          required
        />
        {hasNameError && <div className="error-msg">Please enter a name</div>}
      </div>

      <div className="input-field">
        <label htmlFor="category-description">Description</label>
        <input
          id="category-description"
          type="text"
          value={descriptionInput}
          onChange={handleDescriptionChange}
        />
      </div>

      <div className="input-field">
        <label>Color</label>
        <ColorPalette
          colors={colors}
          selectedColor={colorInput}
          onSelectColor={setColorInput}
        />
      </div>

      <div className="input-field">
        <label>Tags</label>
        <TagSelector
          tags={tagsInput}
          color={colorInput}
          onAddTag={(newTag) => setTagsInput([...tagsInput, newTag])}
          onRemoveTag={(name) =>
            setTagsInput(tagsInput.filter((tag) => tag.name !== name))
          }
          onUpdateTag={({ name, updatedName }) => {
            setTagsInput(
              tagsInput.map((tag) =>
                tag.name === name ? { ...tag, name: updatedName } : tag
              )
            );
          }}
          onRemoveAllTags={() => setTagsInput([])}
          onDuplicateTagError={(message) => setTagExistsError(message)}
        />
      </div>

      <ModalFooter
        actionButtonText="Save"
        actionButtonType="Success"
        onCancelClick={closeModal}
        onActionClick={save}
      />
    </div>
  );
};

export default TagGroupEditModal;
